/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Custom Components for File Upload
 */
import _ from 'lodash';
import { Button, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';

import { getEditBatchRequestPath, getEligibilityRequestsPath, ELIGIBILITY_REQUESTS_TABS } from 'utils/router';
import { ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES } from 'app-constants/eligibility';

const ACCEPTED_FILE_TYPES_LIST = ['.xlsx'];

const ACCEPTED_FILE_TYPES = _.join(ACCEPTED_FILE_TYPES_LIST, ', ');

const MAX_FILE_SIZE = 5;

const MAX_FILE_SIZE_IN_MB = `${MAX_FILE_SIZE}MB`;

// if a batch is already in draft
const EditBatch: React.FC<{ batch?: EligibilityBatchUpload.IBatchEligibilityResponseList; user?: string }> = ({ batch, user }) => {
	const navigate = useNavigate();
	const isDraft = batch?.status === ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.draft;
	if (isDraft) {
		return (
			<div data-testid='batch-edit-draft-banner'>
				{`Batch ${batch?.sequenceId} (created by ${user || <i>{'unknown user'}</i>}) is already in draft. `}
				{
					<Button className='p-0' type='link' onClick={() => navigate(getEditBatchRequestPath(batch?.batchId))}>
						{<u>{'Click here'}</u>}
					</Button>
				}
				{' to continue editing.'}
			</div>
		);
	}
	return null;
};

// if a batch is scheduled or inprogress
const InProgressBatchAlertBar: React.FC<{
	hasInprogressBatch: boolean;
	hasScheduledBatch: boolean;
	type: 'upload' | 'resubmit';
	batch?: EligibilityBatchUpload.IBatchEligibilityResponseList;
	style?: React.CSSProperties;
	className?: string;
}> = ({ hasInprogressBatch, hasScheduledBatch, type, batch, style, className = 'w-1/2' }) => {
	const navigate = useNavigate();
	const isUpload = type === 'upload';
	const isResubmit = type === 'resubmit';
	const batchNumber = batch?.sequenceId;

	if (!hasInprogressBatch && !hasScheduledBatch) {
		return null;
	}

	return (
		<Alert
			showIcon
			message={
				<div className='font-medium'>
					{hasInprogressBatch && `We are currently processing batch ${batchNumber}.`}
					{hasScheduledBatch && `Currently, batch ${batchNumber} has been scheduled.`}
				</div>
			}
			description={
				<div>
					{isUpload && 'You can upload a new batch once the current one is completed. Click '}
					{isResubmit && 'You can resubmit requests once the current batch is completed. Click '}
					{
						<Button
							type='link'
							className='p-0'
							onClick={() => navigate(getEligibilityRequestsPath({ tab: ELIGIBILITY_REQUESTS_TABS.values.batch }))}
						>
							<u>{'here'}</u>
						</Button>
					}
					{' to check the status.'}
				</div>
			}
			type='warning'
			className={`${className} m-auto p-4`}
			style={style}
		/>
	);
};

type FontSizeType = 'xs' | 'sm' | 'base' | 'lg';
const Splitter: React.FC<{ size?: FontSizeType; className?: string }> = ({ size = 'base', className }) => {
	return <span className={`text-[#D4E6FF] text-${size} ${className}`}>{' | '}</span>;
};

const CircularSplitter: React.FC<{ size?: FontSizeType; className?: string }> = ({ size = 'base', className }) => {
	return <span className={`text-[#D4E6FF] text-${size} ${className}`}>{' • '}</span>;
};

export {
	ACCEPTED_FILE_TYPES,
	MAX_FILE_SIZE,
	MAX_FILE_SIZE_IN_MB,
	ACCEPTED_FILE_TYPES_LIST,
	EditBatch,
	InProgressBatchAlertBar,
	Splitter,
	FontSizeType,
	CircularSplitter,
};
